import { useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/pages/Categories";
import { api, formatErr } from "@/lib/api";
import { CheckCircle2, Loader2, Factory, ShieldCheck, FileCheck2, Handshake } from "lucide-react";

const initial = {
  contact_name: "",
  company_name: "",
  email: "",
  phone: "",
  country: "India",
  state_region: "",
  category: "",
  products: "",
  capacity: "",
  certifications: "",
  exports_to: "",
  website: "",
  message: "",
};

const SUPPLIER_CATEGORIES = [
  "Agricultural Commodities",
  "Fresh Produce",
  "Sustainable Packaging",
  "Essential Oils & Botanicals",
  "Industrial & Engineering Products",
  "Precious Metals & Custom Manufacturing",
  "Other",
];

export default function BecomeSupplier() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/suppliers", form);
      setDone(data.id);
      setForm(initial);
    } catch (err) {
      setError(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div data-testid="supplier-success-page">
        <PageHeader eyebrow="APPLICATION RECEIVED" title="Thanks — we've got your details." />
        <section className="max-w-3xl mx-auto px-6 lg:px-8 pb-24">
          <div className="border border-emerald-500/20 bg-emerald-500/[0.04] rounded-lg p-8">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-6 w-6 text-emerald-400" />
              <div className="font-display text-xl font-semibold">
                Reference: <span className="font-mono text-emerald-300">{done.slice(0, 8).toUpperCase()}</span>
              </div>
            </div>
            <p className="mt-4 text-zinc-300 text-[14.5px] leading-relaxed">
              Our India sourcing team will review your application and reach out to verify documents, capacity and references. Typical first response within 2–3 working days.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/" className="inline-flex items-center px-4 py-2.5 rounded-md border border-white/10 hover:bg-white/5 text-sm" data-testid="supplier-success-home">Back to Home</Link>
              <button onClick={() => setDone(null)} className="inline-flex items-center px-4 py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm" data-testid="supplier-success-new">Submit another</button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="supplier-page">
      <PageHeader
        eyebrow="BECOME A SUPPLIER"
        title="Apply to join the VYR Trade supplier network."
        sub="We are actively onboarding Indian manufacturers, growers, exporters and specialist producers. Tell us about your business — we'll verify documents and match you to active buyer briefs."
      />

      <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-24 grid lg:grid-cols-5 gap-8">
        <div className="lg:col-span-2 space-y-4">
          {[
            { icon: Factory, t: "Direct buyer access", d: "We connect verified suppliers to active UK & European procurement briefs — no broker layers." },
            { icon: ShieldCheck, t: "Transparent verification", d: "We coordinate documentation and on-the-ground checks before any introduction." },
            { icon: FileCheck2, t: "Export support", d: "Documentation, certifications and logistics support coordinated case-by-case." },
            { icon: Handshake, t: "Long-term partnerships", d: "We optimise for repeat business and supplier reputation, not one-off deals." },
          ].map((b) => (
            <div key={b.t} className="border border-white/5 bg-zinc-900/40 rounded-lg p-5">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-md border border-amber-400/20 grid place-items-center bg-amber-400/[0.05]">
                  <b.icon className="h-4 w-4 text-amber-400" />
                </div>
                <div className="font-display text-[15.5px] font-semibold">{b.t}</div>
              </div>
              <p className="mt-2 text-[13px] text-zinc-400 leading-relaxed">{b.d}</p>
            </div>
          ))}
        </div>

        <form onSubmit={submit} className="lg:col-span-3 border border-white/10 bg-zinc-900/40 rounded-lg p-6 sm:p-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="supplier-form">
          <Field label="Contact name" required>
            <input data-testid="sf-name" required value={form.contact_name} onChange={onChange("contact_name")} className={inputCls} placeholder="Rajesh Kumar" />
          </Field>
          <Field label="Company name" required>
            <input data-testid="sf-company" required value={form.company_name} onChange={onChange("company_name")} className={inputCls} placeholder="Acme Exports Pvt Ltd" />
          </Field>
          <Field label="Email" required>
            <input data-testid="sf-email" required type="email" value={form.email} onChange={onChange("email")} className={inputCls} placeholder="rajesh@acmeexports.in" />
          </Field>
          <Field label="Phone / WhatsApp" required>
            <input data-testid="sf-phone" required value={form.phone} onChange={onChange("phone")} className={inputCls} placeholder="+91 98765 43210" />
          </Field>
          <Field label="Country" required>
            <input data-testid="sf-country" required value={form.country} onChange={onChange("country")} className={inputCls} placeholder="India" />
          </Field>
          <Field label="State / region">
            <input data-testid="sf-region" value={form.state_region} onChange={onChange("state_region")} className={inputCls} placeholder="Gujarat / Tamil Nadu / Karnataka …" />
          </Field>
          <Field label="Primary category" required>
            <select data-testid="sf-category" required value={form.category} onChange={onChange("category")} className={inputCls}>
              <option value="">Select a category</option>
              {SUPPLIER_CATEGORIES.map((c) => (<option key={c} value={c}>{c}</option>))}
            </select>
          </Field>
          <Field label="Website (optional)">
            <input data-testid="sf-website" value={form.website} onChange={onChange("website")} className={inputCls} placeholder="https://" />
          </Field>
          <Field label="Products you supply" required full>
            <textarea data-testid="sf-products" required rows={3} value={form.products} onChange={onChange("products")} className={inputCls} placeholder="List your main SKUs, materials, finishes, varieties, grades, etc." />
          </Field>
          <Field label="Production capacity">
            <input data-testid="sf-capacity" value={form.capacity} onChange={onChange("capacity")} className={inputCls} placeholder="e.g. 200,000 pcs/month" />
          </Field>
          <Field label="Existing exports to">
            <input data-testid="sf-exports" value={form.exports_to} onChange={onChange("exports_to")} className={inputCls} placeholder="e.g. UK, Germany, UAE" />
          </Field>
          <Field label="Certifications" full>
            <input data-testid="sf-certs" value={form.certifications} onChange={onChange("certifications")} className={inputCls} placeholder="e.g. ISO 9001, BRC, FSSAI, GOTS, IFRA, FSC, CE" />
          </Field>
          <Field label="Anything else?" full>
            <textarea data-testid="sf-message" rows={3} value={form.message} onChange={onChange("message")} className={inputCls} placeholder="References, prior buyers, growth plans, etc." />
          </Field>

          {error && (
            <div className="md:col-span-2 text-sm text-red-400 border border-red-500/30 bg-red-500/[0.04] rounded-md px-3 py-2" data-testid="sf-error">{error}</div>
          )}

          <div className="md:col-span-2 flex items-center justify-between gap-4 pt-1">
            <div className="text-[12px] text-zinc-500">By submitting you agree to our <Link to="/privacy" className="text-zinc-300 hover:text-white">Privacy Policy</Link>.</div>
            <button type="submit" disabled={submitting} className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-blue-600 hover:bg-blue-500 text-white font-semibold text-sm disabled:opacity-60" data-testid="sf-submit">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {submitting ? "Submitting…" : "Submit application"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

const inputCls =
  "w-full bg-zinc-950 border border-white/10 focus:border-amber-400/60 focus:outline-none rounded-md px-3.5 py-2.5 text-[14px] text-zinc-100 placeholder:text-zinc-600 transition-colors";

function Field({ label, required, full, children }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="block text-[12px] font-mono tracking-wide text-zinc-400 mb-1.5">
        {label}{required ? <span className="text-amber-400 ml-1">*</span> : null}
      </label>
      {children}
    </div>
  );
}
